import React, {useEffect, useState} from 'react';
import {Quiz, QuizData, QuizzableType} from "types/Quiz";
import {FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers, FormikProps} from "formik";
import {NewChoiceData, QuizQuestionData} from "types/QuizQuestion";
import Field from "components/Form/Field";
import Button from "components/Button";
import FormControls from "components/FormControls";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Selector from "components/Form/Selector";
import Uploader from "components/Form/Uploader";
import FieldHeader from "components/Form/FieldHeader";
import {PhotographIcon} from "@heroicons/react/outline";
import ButtonGroup from "components/ButtonGroup";
import {ExclamationCircleIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Well from "components/Well";
import Toggle from "components/Form/Toggle";
import {toInt} from 'services/number';
import {useOperator} from "../../../context/OperatorContext";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {TextAreaField} from "../../../components/Form/Form";
import {useAuth} from "../../../context/AuthContext";
import {QuizType} from "../../../types/QuizType";
import {Item} from "../../../components/Form/Selector/Selector";
import {TrailWaypoint} from "../../../types/TrailWaypoint";
import {Trail} from "../../../types/Trail";
import {Facility} from "../../../types/Facility";
import {Zone} from "../../../types/Zone";
import {Exhibit} from "../../../types/Exhibit";
import {SiteAttachmentFieldSet} from "../../../components/Form/Form/Fieldset";
import {GageGenerateContent} from "../../../components/GageGenerateContent";
import {AIContent, QuizAIContentAPIData} from "../../../types/AIContent";
import {getAIContentStatus} from "../../../services/ai-content";

// Types ----

type QuizFormProps = {
  quiz?: Quiz | null
  onSubmit: (values: QuizData, helpers: FormikHelpers<QuizData>) => any
}

// Helpers ----

function letterAtIndex(index: number): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  return alphabet[index % alphabet.length];
}

function createNewChoice(): NewChoiceData {
  return {
    key: crypto.randomUUID(), // 'key' will need to be stripped out of data before sending to API (used for 'key' prop only)
    text: '',
    is_correct: false
  }
}

const newQuestion: QuizQuestionData = {
  text: '',
  choices: [createNewChoice(), createNewChoice()]
}

function quizTypeAsItem(quizType: QuizType): Item {
  return {
    id: quizType.id || 0,
    name: quizType.name,
  }
}

function exhibitAsItem(exhibit: Exhibit): Item {
  return {
    id: exhibit.id || 0,
    name: exhibit.name,
    description: exhibit.zone.site.name ? `(${exhibit.zone.site.name})` : undefined,
  }
}

function facilityAsItem(facility: Facility): Item {
  return {
    id: facility.id || 0,
    name: facility.name,
    description: facility.zone.site.name ? `(${facility.zone.site.name})` : undefined,
  }
}

function zoneAsItem(zone: Zone): Item {
  return {
    id: zone.id || 0,
    name: zone.name,
    description: zone.site.name ? `(${zone.site.name})` : undefined,
  }
}

function trailAsItem(trail: Trail): Item {
  return {
    id: trail.id || 0,
    name: trail.name,
    description: trail.site?.name ? `(${trail.site.name})` : undefined,
  }
}

function trailWaypointAsItem(trailWaypoint: TrailWaypoint, index: number = 0): Item {
  const waypointNumber = index + 1;

  return {
    id: trailWaypoint.id || 0,
    name: `${waypointNumber} - ${trailWaypoint.name}`,
  }
}

// Component ----

function QuizForm(props: QuizFormProps) {
  const {termFor} = useOperator();
  const auth = useAuth();
  const [maxAttemptsMode, setMaxAttemptsMode] = useState<string>(props.quiz?.maximum_attempts ? 'limited' : 'unlimited');
  const [quizzableType, setQuizzableType] = useState<QuizzableType | null>(props.quiz?.quizzable_type ?? null);
  const [quizzableId, setQuizzableId] = useState<number | null>(props.quiz?.quizzable?.id ?? null);
  const [trailId, setTrailId] = useState<number | null>((props.quiz?.quizzable_type === 'trail-waypoint' && props.quiz?.quizzable?.trail?.id) ? props.quiz?.quizzable?.trail?.id : null);

  const {data: quizTypes, isLoading: typesLoading} = useQuery(
    ['quiz-types.all', {page: 1}],
    () => ngage.quizTypes.all(1),
    {enabled: auth.can('quiz-types:read')}
  )

  const {data: exhibits, isLoading: exhibitsLoading} = useQuery(
    ['exhibits.all', {page: 1, perPage: 500}],
    () => ngage.exhibits.filtered({page: 1, per_page: 500}),
    {enabled: auth.can('exhibits:read') && quizzableType === 'exhibit'}
  )

  const {data: facilities, isLoading: facilitiesLoading} = useQuery(
    ['facilities.all', {page: 1, perPage: 500}],
    () => ngage.facilities.filtered({page: 1, per_page: 500}),
    {enabled: auth.can('facilities:read') && quizzableType === 'facility'}
  )

  const {data: zones, isLoading: zonesLoading} = useQuery(
    ['zones.all', {page: 1, perPage: 500}],
    () => ngage.zones.filtered({page: 1, per_page: 500}),
    {enabled: auth.can('zones:read') && quizzableType === 'zone'}
  )

  const {data: trails, isLoading: trailsLoading} = useQuery(
    ['trails.all', {page: 1, perPage: 500}],
    () => ngage.trails.filtered({page: 1, perPage: 500}),
    {enabled: auth.can('trails:read') && quizzableType === 'trail-waypoint'}
  )

  const {data: trailWaypoints, isLoading: trailWaypointsLoading, refetch: refetchTrailWaypoints, isRefetching: isTrailWaypointsRefetching} = useQuery(
    ['trailWaypoints.all', {page: 1, perPage: 500}],
    () => ngage.trailWaypoints.filtered(trailId ?? 0, {page: 1, perPage: 500}),
    {
      enabled: auth.can('trails:read') && quizzableType === 'trail-waypoint' && !!trailId,
      refetchOnWindowFocus: false
    }
  )

  const createAIContent = useMutation((data: QuizAIContentAPIData) => ngage.aiContent.create(data), {
    onError: error => {
      console.error('Error creating AI content:', error);
    }
  });

  // If trail is changed, need to refetch waypoints for that trail
  useEffect(() => {
    if (!trailId) return;

    void refetchTrailWaypoints();
  }, [trailId, refetchTrailWaypoints]);

  const quizzableTypeItems: Item[] = [
    {id: 'exhibit', name: termFor('Exhibit')},
    {id: 'zone', name: termFor('Zone')},
    {id: 'facility', name: termFor('Facility')},
    {id: 'trail-waypoint', name: `${termFor('Trail')} Waypoint`},
  ]

  const validationSchema = Yup.object().shape({
    type_id: Yup.number().required('This field is required'),
    title: Yup.string().required("This field is required").max(255, 'Maximum character limit exceeded'),
    description: Yup.string().max(255, 'Maximum character limit exceeded'),
    maximum_attempts: Yup.number().min(1, "Must be at least 1").required('This field is required').nullable(),
    questions: Yup.array().of(
      Yup.object().shape({
        text: Yup.string().required("This field is required").max(255, 'Maximum character limit exceeded'),
        choices: Yup.array().of(
          Yup.object().shape({
            text: Yup.string().required("This field is required").max(255, 'Maximum character limit exceeded'),
            is_correct: Yup.boolean()
          })
        )
          .test(function (choices) {
            const { path, createError } = this;

            if (!choices?.some(c => c.is_correct)) {
              return createError({
                path: `${path}OneCorrect`,
                message: 'At least one choice must be marked as correct'
              });
            }
            return true;
          })
      })
    )
  })

  const initialValues: QuizData = {
    type_id: props.quiz?.type?.id,
    quizzable: (props.quiz?.quizzable_type && props.quiz?.quizzable) ?
      {type: props.quiz.quizzable_type, id: props.quiz.quizzable.id} :
      null,
    title: props.quiz?.title || '',
    description: props.quiz?.description || '',
    image_id: props.quiz?.image?.id || undefined,
    maximum_attempts: props.quiz?.maximum_attempts || null,
    questions: props.quiz?.questions.map(question =>
      ({id: question.id, text: question.text, choices: question.choices})
    ) || [{...newQuestion}],
    site_ids: props.quiz?.sites.map(site => site.id)
  }

  async function selectQuizType(typeId: number, {setFieldValue}: FormikProps<QuizData>) {
    void setFieldValue('type_id', typeId);
  }

  function selectQuizzableType(type: QuizzableType | null, {setFieldValue}: FormikHelpers<QuizData>) {
    void setFieldValue('quizzable', null);
    setQuizzableType(type);
    setQuizzableId(null);
    setTrailId(null);
    createAIContent.reset();
  }

  function selectQuizzable(type: QuizzableType | null, id: string | number | null, {setFieldValue}: FormikHelpers<QuizData>) {
    if (type && id) {
      void setFieldValue('quizzable', {type: type, id: toInt(id)})
      setQuizzableId(toInt(id));
    } else {
      void setFieldValue('quizzable', null);
      setQuizzableId(null);
      setTrailId(null);
    }
  }

  function selectMaximumAttempts(mode: string, {setFieldValue}: FormikHelpers<QuizData>) {
    setMaxAttemptsMode(mode)
    void setFieldValue('maximum_attempts', mode === 'limited' ? 1 : null)
  }

  function handleRemoveAttachment(formik: FormikHelpers<QuizData>) {
    setQuizzableType(null);
    selectQuizzable(null, null, formik);
  }

  async function handleAIContent(formik: FormikProps<QuizData>) {
    if (!quizzableId || !quizzableType) return;

    createAIContent.mutateAsync({
      content_type: 'quiz',
      quizzable_type: quizzableType,
      quizzable_id: quizzableId,
    }).then(aiContent => {
      // TODO: Remove this once the AI response no longer intemittently returns an array
      // Defensive logic to prevent app crashing if `content` property is an empty array from the AI response
      if (Array.isArray(aiContent.content)) return;

      // Set form fields data
      setAIContentFormFields(aiContent, formik);
    }).catch(() => null); // Error handled in useMutation onError callback
  }

  function setAIContentFormFields(data: AIContent, formik: FormikProps<QuizData>) {
    if (!data || !data.content || data.content_type !== 'quiz') return;

    // Remove 'order' property from the question
    const questions = data.content.questions.map(question => {
      const {order, ...modifiedQuestion} = question;

      const choices = modifiedQuestion.choices.map(choice => {
        return {...choice, key: crypto.randomUUID()} // 'key' used for UI only
      })

      return {...modifiedQuestion, choices};
    });

    formik.setFieldValue('title', data.content.title).catch(console.error);
    formik.setFieldValue('description', data.content.description).catch(console.error);
    formik.setFieldValue('questions', questions).catch(console.error);
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  {/* Attached entity */}
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Attached entity
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose what entity this {termFor('quiz')} will be attached to (optional).
                      </p>
                    </div>

                    {/* Quizzable */}
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className='sm:col-start-1 sm:col-span-3'>
                        <Selector
                          label='Type'
                          items={quizzableTypeItems}
                          selectedId={quizzableType}
                          onChange={item => selectQuizzableType(item.id as QuizzableType, formik)}
                        />
                      </div>

                      {quizzableType === 'exhibit' &&
                        <div className='sm:col-start-1 sm:col-span-3'>
                          <Selector
                            label={`${termFor('Exhibit')} *`}
                            isLoading={exhibitsLoading}
                            items={exhibits?.data?.map(exhibitAsItem) || []}
                            selectedId={quizzableId}
                            onChange={item => selectQuizzable('exhibit', item.id, formik)}
                            disabled={exhibitsLoading}
                          />
                        </div>
                      }

                      {quizzableType === 'facility' &&
                        <div className='sm:col-start-1 sm:col-span-3'>
                          <Selector
                            label={`${termFor('Facility')} *`}
                            isLoading={facilitiesLoading}
                            items={facilities?.data?.map(facilityAsItem) || []}
                            selectedId={quizzableId}
                            onChange={item => selectQuizzable('facility', item.id, formik)}
                            disabled={facilitiesLoading}
                          />
                        </div>
                      }

                      {quizzableType === 'zone' &&
                        <div className='sm:col-start-1 sm:col-span-3'>
                          <Selector
                            label={`${termFor('Zone')} *`}
                            isLoading={zonesLoading}
                            items={zones?.data?.map(zoneAsItem) || []}
                            selectedId={quizzableId}
                            onChange={item => selectQuizzable('zone', item.id, formik)}
                            disabled={zonesLoading}
                          />
                        </div>
                      }

                      {quizzableType === 'trail-waypoint' &&
                        <>
                          <div className='sm:col-start-1 sm:col-span-3'>
                            <Selector
                              label={`${termFor('Trail')} *`}
                              isLoading={trailsLoading}
                              items={trails?.data?.map(trailAsItem) || []}
                              selectedId={trailId}
                              onChange={item => {
                                setTrailId(toInt(item.id));
                              }}
                              disabled={trailsLoading}
                            />
                          </div>

                          <div className='sm:col-start-1 sm:col-span-3'>
                            <Selector
                              label='Waypoint *'
                              isLoading={trailWaypointsLoading || isTrailWaypointsRefetching}
                              items={trailWaypoints?.data?.map((item, index) => trailWaypointAsItem(item, index)) || []}
                              selectedId={quizzableId}
                              onChange={item => selectQuizzable('trail-waypoint', item.id, formik)}
                              disabled={!trailId || trailWaypointsLoading || isTrailWaypointsRefetching}
                            />
                          </div>
                        </>
                      }

                      <div className='sm:col-start-1  sm:col-span-3'>
                        <Button
                          color={Button.colors.WHITE}
                          size={Button.sizes.X_SMALL}
                          icon={TrashIcon}
                          disabled={!formik.values.quizzable}
                          onClick={() => handleRemoveAttachment(formik)}
                        >
                          Remove entity
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className='pt-8'>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details about the {termFor('quiz')}
                      </p>
                    </div>

                    {(!props.quiz && quizzableType === 'exhibit' && quizzableId) &&
                      <div className="pt-8 pb-5">
                        <GageGenerateContent
                          onClick={() => handleAIContent(formik)}
                          loading={createAIContent.isLoading}
                          disabled={!quizzableId}
                          status={getAIContentStatus(createAIContent)}
                          contentFields={['Title', 'Description', 'Questions', 'Choices']}
                        />
                      </div>
                    }

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      {/* Type */}
                      <div className="sm:col-span-2">
                        <Selector
                          label={`${termFor('Quiz Type')} *`}
                          isLoading={typesLoading}
                          items={quizTypes?.data.map(quizTypeAsItem) || []}
                          selectedId={initialValues.type_id}
                          onChange={selected => selectQuizType(Number(selected.id), formik)}
                        />

                        {(!!formik.errors.type_id && formik.touched.type_id) &&
                          <FieldHelp isError>
                            {formik.errors.type_id}
                          </FieldHelp>
                        }
                      </div>

                      {/* Title */}
                      <div className="sm:col-span-4 sm:col-start-1">
                        <Field
                          type="text"
                          label="Title *"
                          hint={'255 chars max'}
                          name="title"
                          id="title"
                          autoComplete="off"
                          maxLength="255"
                        />
                      </div>

                      {/* Description */}
                      <div className="sm:col-span-6">
                        <TextAreaField
                          label="Description"
                          hint="Optional. 255 chars max."
                          name="description"
                          id="description"
                          autoComplete="off"
                          maxChars={255}
                          rows={3}
                        />
                      </div>

                      {/* Image */}
                      <div className="sm:col-span-6">
                        <FieldHeader label="Image"/>
                        <Uploader
                          icon={PhotographIcon}
                          type={Uploader.types.IMAGE}
                          maxFiles={1}
                          files={props.quiz?.image || undefined}
                          onChange={files => formik.setFieldValue('image_id', files[0]?.id || null)}
                        />
                      </div>

                      {/* Max Attempts */}
                      <div className="sm:col-span-2">
                        <FieldHeader label="Maximum Attempts"/>
                        <div className="mt-1">
                          <ButtonGroup
                            selectedId={maxAttemptsMode}
                            onChange={option => selectMaximumAttempts(option.id, formik)}>
                            {[
                              {id: "unlimited", name: 'Unlimited'},
                              {id: 'limited', name: 'Limited'},
                            ]}
                          </ButtonGroup>

                          {maxAttemptsMode === 'limited' ?
                            <div className="sm:w-48">
                              <Field
                                type="number"
                                name="maximum_attempts"
                                id="maximum_attempts"
                                min={0}
                                max={65535}
                              />
                            </div>
                            : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Questions
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Prepare the questions that make up this {termFor('quiz')}
                      </p>
                    </div>
                    <FieldArray name="questions">
                      {({push, remove}: FieldArrayRenderProps) => (
                        <div>
                          <ul>
                            {formik.values.questions.map((question, index) => (
                              <li key={index} className="my-4">
                                <Well
                                  heading={`Question ${index + 1}`}
                                  action={
                                    <Button
                                      color={Button.colors.WHITE}
                                      size={Button.sizes.X_SMALL}
                                      icon={TrashIcon}
                                      onClick={() => remove(index)}
                                      disabled={formik.values.questions.length < 2}
                                    />
                                }>
                                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                    {/* Text */}
                                    <div className="sm:col-span-6">
                                      <Field
                                        type="text"
                                        label="Question Text *"
                                        hint='255 chars max'
                                        name={`questions.${index}.text`}
                                        id={`questions.${index}.text`}
                                        autoComplete="off"
                                        maxLength="255"
                                      />
                                    </div>

                                    {/* Question choices */}
                                    <FieldArray name={`questions.${index}.choices`}>
                                      {({push: pushChoice, remove}) => (
                                        <div className="sm:col-span-6">
                                          <ul className="grid grid-cols-1 gap-y-6 gap-x-10 sm:grid-cols-6">
                                            {question.choices.map((choice, choiceIndex) => {
                                              const key = ('key' in choice && choice.key) || ('uuid' in choice && choice.uuid) || choiceIndex;

                                              return (
                                                <li key={key} className="sm:col-span-3 flex space-x-2">
                                                  <div className="w-full">
                                                    <Field
                                                      type="text"
                                                      label={`Choice ${letterAtIndex(choiceIndex)} *`}
                                                      name={`questions.${index}.choices.${choiceIndex}.text`}
                                                      id={`questions.${index}.choices.${choiceIndex}.text`}
                                                      maxLength={255}
                                                    />
                                                  </div>

                                                  <div>
                                                    <FieldHeader label="Correct"/>
                                                    <div className="mt-2">
                                                      <Toggle
                                                        checked={choice.is_correct}
                                                        onChange={checked => formik.setFieldValue(`questions.${index}.choices.${choiceIndex}.is_correct`, checked)}
                                                      />
                                                    </div>
                                                  </div>

                                                  <div className="mt-8">
                                                    <Button
                                                      color={Button.colors.WHITE}
                                                      size={Button.sizes.TINY}
                                                      icon={TrashIcon}
                                                      onClick={() => remove(choiceIndex)}
                                                      disabled={question.choices.length <= 2}
                                                    />
                                                  </div>
                                                </li>
                                              )
                                            })}
                                          </ul>

                                          <div className="mt-6">
                                            <Button
                                              color={Button.colors.WHITE}
                                              icon={PlusIcon}
                                              disabled={question.choices.length >= 8}
                                              onClick={() => pushChoice(createNewChoice())}
                                            >
                                              Add choice
                                            </Button>

                                            <p className="text-sm text-gray-500 mt-2">Maximum of 8 choices</p>

                                            {/* @ts-ignore */}
                                            {(!!formik.submitCount && !formik.errors.questions?.[index]?.choices?.length && !!formik.errors.questions?.[index]?.choicesOneCorrect) &&
                                              <div className='mt-4'>
                                                <FieldHelp isError>
                                                  <div className="flex items-center gap-2">
                                                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                                                    {/* @ts-ignore */}
                                                    {formik.errors.questions?.[index].choicesOneCorrect}
                                                  </div>
                                                </FieldHelp>
                                              </div>
                                            }
                                          </div>
                                        </div>
                                      )}
                                    </FieldArray>
                                  </div>
                                </Well>
                              </li>
                            ))}
                          </ul>

                          <div className="mt-4">
                            <Button
                              color={Button.colors.SECONDARY}
                              icon={PlusIcon}
                              onClick={() => push({...newQuestion})}
                            >
                              Add Question
                            </Button>
                          </div>
                        </div>
                      )}
                    </FieldArray>
                  </div>

                  {/* Sites */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {termFor('Sites')}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose which {termFor('sites')} to associate this with.
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <SiteAttachmentFieldSet name={"site_ids"} selected={props.quiz?.sites}/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <FormControls formik={formik}/>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

// Export ----

export default QuizForm;